import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import Metric from './Metric';
import { LottieLoader } from '../../utilities/Loaders';
import { truncateNumber } from '../../utilities/Utils';
import TopIndicesChart from './TopIndicesChart';
import LastUpdatedIndicator from './LastUpdatedIndicator';
import { percentageIndicatorClass, percentageIndicator } from '../Accounts/Assets/AssetUtils';
import markets from '../../client-api/modules/Markets';
import { MarketClosedMessage } from './MarketTime';

const HomeMarketPerformanceChart = () => {
    const [marketPerformance, setMarketPerformance] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const getHomePagePerformance = async () => {
        try {
            const data = await markets.getHomepagePerformance();

            if (_.isEmpty(data)) {
                setHasError(true);
                setIsLoading(false);
                return;
            }

            setMarketPerformance(data);
        } catch (error) {
            setHasError(true);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        (async () => {
            await getHomePagePerformance();
        })();
    }, []);

    if (isLoading) {
        return <LottieLoader />;
    }

    if (hasError) {
        return (
            <>
                <span className="icon error-data home-market-error-state"/>
                <span>Unable to load market data</span>
            </>
        );
    }

    const {
        volume, rises, falls, instrument, turnover, asAt, isMarketOpen, marketOpeningLabel,
    } = marketPerformance;

    const changePercentageIndicator = percentageIndicator.parse(instrument.changePercentage);
    const turnoverMetricValue = `${ truncateNumber(turnover.amount) } ${ turnover.currency }`;

    return (
        <>
            <div className={ 'nzx-today mb-3' }>
                <div>
                    <span aria-label={'market closed'}>
                        <MarketClosedMessage
                            isMarketOpen={isMarketOpen}
                            openingLabel={marketOpeningLabel}
                            marketGroup={'NZX'}
                        />
                    </span>
                </div>
                {!isMarketOpen && <MarketOpenHours />}
            </div>
            <div className="performance-metrics-container">
                { isMarketOpen && (
                    <>
                        <Metric label="Rises" value={ rises }/>
                        <Metric label="Falls" value={ falls }/>
                        <Metric label="Volume" value={ truncateNumber(volume) }/>
                        <Metric label="Turnover" value={ turnoverMetricValue }/>

                        <MarketOpenHours />
                    </>
                ) }
            </div>
            <h3 className={ 'pb-2' }>NZX Market performance</h3>
            <div className="top-indices">
                <div className="d-flex flex-wrap justify-content-between">
                    {instrument.name && (
                        <div>
                            <p>{instrument.name}</p>
                        </div>
                    )}
                    <div>
                        <div>
                            <p>
                                {truncateNumber(instrument.closeValue.amount)}
                                {instrument.closeValue.currency}
                            </p>
                        </div>
                        <div className="changes">
                            {`${instrument.changeValue.amount > 0 ? '+' : ''}
                                ${truncateNumber(instrument.changeValue.amount)}
                                ${instrument.changeValue.currency}
                                (${instrument.changePercentage.toFixed(2)}%)`}
                            <span className={percentageIndicatorClass(changePercentageIndicator)} />
                        </div>
                    </div>
                </div>
            </div>
            <TopIndicesChart values={instrument.prices.values} />
            <LastUpdatedIndicator date={asAt} />
        </>
    );
};

const MarketOpenHours = () => {
    return (
        <div className={ 'hours' }>
            <span className={ 'font-weight-bold' }>Market hours:</span> Mon - Fri, 10:00am - 5:00pm
        </div>
    );
};

export default HomeMarketPerformanceChart;
